import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Select, Space, Table, Tag, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, ExportOutlined, ImportOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiErrorMessage,
  COMPETENCY_PERMS as P,
  competencyKeys,
  useCompetencyCan,
  useTrainingCourses,
  useTrainingSessions,
} from '@/hooks/useCompetency';
import { competencyService, type TrainingSession } from '@/services/competency.service';
import { triggerDownload } from '@/reports/utils/exportTable';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, STATUS_COLOR } from './competencyDisplay';

const TrainingSessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = useCompetencyCan(P.trainingManage);
  const canImport = useCompetencyCan(P.import);
  const canView = useCompetencyCan(P.trainingView);
  const canExport = canImport || canManage || canView;
  const [status, setStatus] = useState<string>();
  const [courseId, setCourseId] = useState<string>();
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<'template' | 'export' | 'import' | null>(null);
  const { data = [], isLoading, refetch } = useTrainingSessions({ status, course_id: courseId, search });
  const { data: courses = [] } = useTrainingCourses();

  const columns: ColumnsType<TrainingSession> = [
    {
      title: 'Session',
      dataIndex: 'session_number',
      width: 150,
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/sessions/${r.id}`)}>{v}</a>,
    },
    { title: 'Date', dataIndex: 'session_date', width: 110, render: (v) => formatDate(v) },
    { title: 'Training', render: (_, r) => `${r.course_code ?? ''} ${r.course_title ?? ''}`.trim() },
    { title: 'Topic', dataIndex: 'topic', ellipsis: true },
    { title: 'Trainer', dataIndex: 'trainer_display', width: 170 },
    { title: 'Attendees', width: 110, align: 'right', render: (_, r) => `${r.present_count ?? 0} / ${r.attendee_count ?? 0}` },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
    {
      title: 'Effectiveness',
      width: 140,
      render: (_, r) =>
        r.evaluation_id ? (
          <a onClick={() => navigate(`${COMPETENCY_BASE}/effectiveness/${r.evaluation_id}`)}>
            <Tag color={STATUS_COLOR[r.evaluation_status ?? '']}>{r.evaluation_status}</Tag>
          </a>
        ) : (
          '-'
        ),
    },
  ];

  const downloadTemplate = async () => {
    setBusy('template');
    try {
      triggerDownload(await competencyService.downloadImportTemplate('sessions'), 'competency-sessions-template.xlsx');
      message.success('Template downloaded');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const exportCurrent = async () => {
    setBusy('export');
    try {
      triggerDownload(await competencyService.exportSessions(), 'training-attendance-export.xlsx');
      message.success('Export started');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const importFile = async (file: File) => {
    setBusy('import');
    try {
      const check = await competencyService.runImport('sessions', file, true);
      if (check.errors.length) {
        message.error(
          `Check failed: ${check.errors[0].message}${check.errors.length > 1 ? ` (+${check.errors.length - 1} more)` : ''}`
        );
        navigate(`${COMPETENCY_BASE}/import?kind=sessions`);
        return false;
      }
      const result = await competencyService.runImport('sessions', file, false);
      message.success(`Imported: ${result.created} created, ${result.updated} updated`);
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      void refetch();
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setBusy(null);
    }
    return false;
  };

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Training attendance"
        extra={
          <Space wrap>
            {canExport ? (
              <Button icon={<ExportOutlined />} loading={busy === 'export'} onClick={() => void exportCurrent()}>
                Export
              </Button>
            ) : null}
            {canImport ? (
              <>
                <Button icon={<DownloadOutlined />} loading={busy === 'template'} onClick={() => void downloadTemplate()}>
                  Template
                </Button>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={(f) => {
                    void importFile(f);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={busy === 'import'}>
                    Import
                  </Button>
                </Upload>
                <Link to={`${COMPETENCY_BASE}/import?kind=sessions`}>
                  <Button icon={<ImportOutlined />}>Bulk import wizard</Button>
                </Link>
              </>
            ) : null}
            {canManage ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`${COMPETENCY_BASE}/sessions/new`)}>
                New session
              </Button>
            ) : null}
          </Space>
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search session, topic or trainer" allowClear style={{ width: 280 }} onSearch={setSearch} />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 150 }}
            value={status}
            onChange={setStatus}
            options={['Planned', 'Completed', 'Cancelled'].map((s) => ({ value: s, label: s }))}
          />
          <Select
            allowClear
            placeholder="Course"
            style={{ width: 260 }}
            value={courseId}
            onChange={setCourseId}
            showSearch
            optionFilterProp="label"
            options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
          />
        </Space>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} />
      </Card>
    </div>
  );
};

export default TrainingSessionsPage;
